// (Not named *Switch: the module-switch drift guard owns that name for the in-module page navs.)
// The two studies of EOS-11 (A32): the power factor correction of a bus (11a) and the
// harmonic assessment at the point of common coupling (11b). The mode is part of the page's
// address (?mode=), so Back, Forward and a shared link bring the same mode back.

export const POWER_QUALITY_MODES = ["pf", "harmonics"] as const;

export type PowerQualityMode = (typeof POWER_QUALITY_MODES)[number];

export const DEFAULT_POWER_QUALITY_MODE: PowerQualityMode = "pf";

/** The mode a query value names; a missing or unknown value is the default, pf. */
export function powerQualityModeOf(value: string | null): PowerQualityMode {
  return (POWER_QUALITY_MODES as readonly string[]).includes(value ?? "")
    ? (value as PowerQualityMode)
    : DEFAULT_POWER_QUALITY_MODE;
}

// Exhaustive map: a new mode without words is a compile error.
export const POWER_QUALITY_MODE_WORDS: Record<PowerQualityMode, { name: string; description: string }> = {
  pf: {
    name: "Power factor correction",
    description: "Size the compensation of a bus, the bank and the resonance screening",
  },
  harmonics: {
    name: "Harmonic assessment",
    description: "THDi, TDD, THDv and Isc / IL at the point of common coupling",
  },
};

type PowerQualityModePickerProps = {
  mode: PowerQualityMode;
  onChange: (mode: PowerQualityMode) => void;
};

// A native radio group: one tab stop, the arrow keys move the choice, the name and the
// one-line description of each mode are read out. Changing the mode never touches a draft:
// each form keeps its own under its own module key.
export function PowerQualityModePicker({ mode, onChange }: PowerQualityModePickerProps) {
  return (
    <fieldset data-power-quality-mode-picker>
      <legend>Mode</legend>
      {POWER_QUALITY_MODES.map((option) => (
        <div key={option} data-power-quality-mode={option}>
          <label>
            <input
              type="radio"
              name="power-quality-mode"
              value={option}
              checked={mode === option}
              aria-describedby={`power-quality-mode-${option}-description`}
              onChange={() => onChange(option)}
            />
            {POWER_QUALITY_MODE_WORDS[option].name}
          </label>
          <p id={`power-quality-mode-${option}-description`}>
            {POWER_QUALITY_MODE_WORDS[option].description}
          </p>
        </div>
      ))}
    </fieldset>
  );
}

export default PowerQualityModePicker;
